import { useCallback, useState } from 'react';

import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';
import { useBoolean } from 'src/hooks/use-boolean';

import { isAdmin } from 'src/services/auth/auth.utils';
import {
  useDeleteEmailMessageTemplateMutation,
  useGetEmailMessageTemplatesQuery
} from 'src/services/email-sync/email-sync.service';
import {
  EmailMessageTemplate,
  EmailMessageTemplateType,
  TemplateVisibility
} from 'src/services/email-sync/email-sync.types';

import { useSnackbar } from 'notistack';
import { ConfirmDialog } from 'src/components/custom-dialog';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import {
  emptyRows,
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable
} from 'src/components/table';

import { t } from 'i18next';
import { Stack } from '@mui/system';
import { Typography } from '@mui/material';
import EmailTemplateEditor from './email-template-editor';
import EmailTemplateTableRow from './email-template-table-row';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'name', label: t('email_sync.templates.template_name') },
  { id: 'type', label: t('email_sync.templates.template_type') },
  { id: 'visibility', label: t('email_sync.templates.template_visibility') },
  { id: 'created_at', label: t('email_sync.templates.created_at') },
  { id: '', width: 88 },
];

const defaultFilters = {
  name: '',
  type: '' as EmailMessageTemplateType | '',
};

// ----------------------------------------------------------------------

export default function EmailTemplateManager() {

  const { enqueueSnackbar } = useSnackbar();
  const authContext = useAuthContext();

  const table = useTable();

  const [filters, setFilters] = useState(defaultFilters);

  const editorModal = useBoolean();
  const confirmDialog = useBoolean();

  const [selectedTemplate, setSelectedTemplate] = useState<EmailMessageTemplate | null>(null);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const {
    data: templatesData,
    isLoading,
    refetch
  } = useGetEmailMessageTemplatesQuery({
    page,
    per_page: perPage,
    type: filters.type || undefined,
  });

  const [deleteTemplate] = useDeleteEmailMessageTemplateMutation();

  const denseHeight = table.dense ? 52 : 72;

  const canDelete = useCallback((template: EmailMessageTemplate) => {
    if (!authContext) return false;

    // Admins can delete any template
    if (isAdmin(authContext)) return true;

    // Non-admins can only delete their own account-specific templates
    return template.visibility === TemplateVisibility.ACCOUNT_SPECIFIC;
  }, [authContext]);

  const canEdit = useCallback((template: EmailMessageTemplate) => {
    if (!authContext) return false;

    // Admins can edit any template
    if (isAdmin(authContext)) return true;

    // Non-admins can only edit their own account-specific templates
    return template.visibility === TemplateVisibility.ACCOUNT_SPECIFIC;
  }, [authContext]);

  const handleFilters = useCallback(
    (name: string, value: string) => {
      table.onResetPage();
      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage);
  };

  const handlePerPageChange = (event: any) => {
    setPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleDeleteRow = useCallback(async () => {
    if (!templateToDelete) return;

    try {
      await deleteTemplate(templateToDelete).unwrap();
      enqueueSnackbar(t('email_sync.templates.api.delete.success'), { variant: 'success' });
      refetch();
      confirmDialog.onFalse();
      setTemplateToDelete(null);
    } catch (error) {
      enqueueSnackbar(t('email_sync.templates.api.delete.default_error'), { variant: 'error' });
    }
  }, [templateToDelete, deleteTemplate, enqueueSnackbar, refetch, confirmDialog]);

  const handleEditRow = useCallback((template: EmailMessageTemplate) => {
    if (!canEdit(template)) {
      enqueueSnackbar(t('email_sync.templates.permissions.cannot_edit'), { variant: 'warning' });
      return;
    }
    setSelectedTemplate(template);
    editorModal.onTrue();
  }, [canEdit, enqueueSnackbar, editorModal]);

  const handleDeleteConfirm = useCallback((templateId: string, template: EmailMessageTemplate) => {
    if (!canDelete(template)) {
      enqueueSnackbar(t('email_sync.templates.permissions.cannot_delete'), { variant: 'warning' });
      return;
    }
    setTemplateToDelete(templateId);
    confirmDialog.onTrue();
  }, [canDelete, enqueueSnackbar, confirmDialog]);

  const handleAddNew = useCallback(() => {
    setSelectedTemplate(null);
    editorModal.onTrue();
  }, [editorModal]);

  const handleCloseEditor = useCallback(() => {
    setSelectedTemplate(null);
    editorModal.onFalse();
    refetch();
  }, [editorModal, refetch]);

  const notFound = !templatesData?.count;

  return (
    <>
      <Card>
        <CardHeader
          title={
            <Stack direction="column" alignItems="start" justifyContent="center" mb={2}>
              <Typography variant='h6'>
                {t('email_sync.templates.title')}
              </Typography>
              <Typography variant='body2' color="text.secondary">
                {t('email_sync.templates.subtitle')}
              </Typography>
            </Stack>
          }
          action={
            <Button
              variant="contained"
              startIcon={<Iconify icon="mingcute:add-line" />}
              onClick={handleAddNew}
            >
              {t('email_sync.templates.add_template')}
            </Button>
          }
        />

        {
          isLoading ? (
            <TableSkeleton />
          )
            :
            <>
              <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
                <Scrollbar>
                  <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
                    <TableHeadCustom
                      order={table.order}
                      orderBy={table.orderBy}
                      headLabel={TABLE_HEAD}
                      rowCount={templatesData?.count || 0}
                      numSelected={table.selected.length}
                      onSort={table.onSort}
                    />

                    <TableBody>
                      {templatesData?.results.map((row) => (
                        <EmailTemplateTableRow
                          key={row.id}
                          row={row}
                          selected={table.selected.includes(row.id)}
                          onEditRow={() => handleEditRow(row)}
                          onDeleteRow={() => handleDeleteConfirm(row.id, row)}
                        />
                      ))}

                      <TableEmptyRows
                        height={denseHeight}
                        emptyRows={emptyRows(table.page, table.rowsPerPage, templatesData?.results.length || 0)}
                      />

                      <TableNoData notFound={notFound} />
                    </TableBody>
                  </Table>
                </Scrollbar>
              </TableContainer>
            </>
        }

        <TablePaginationCustom
          count={templatesData?.count || 0}
          page={page}
          rowsPerPage={perPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePerPageChange}
          dense={table.dense}
          onChangeDense={table.onChangeDense}
        />
      </Card>

      <EmailTemplateEditor
        open={editorModal.value}
        onClose={handleCloseEditor}
        template={selectedTemplate || undefined}
      />

      <ConfirmDialog
        open={confirmDialog.value}
        onClose={confirmDialog.onFalse}
        title={t('email_sync.templates.delete_template')}
        content={t('email_sync.templates.delete_confirm_description')}
        action={
          <Button variant="contained" color="error" onClick={handleDeleteRow}>
            {t('common.delete')}
          </Button>
        }
      />
    </>
  );
}